import { useTranslation } from 'react-i18next';
import { useDebounceValue } from 'usehooks-ts';

import { queries } from 'RestAPIs/rapini';

import type { PlanningEntityType, SchedulingLabel } from 'generated/index';

import { sortLabel } from 'components/mrp/components/AppointmentForm/AppointmentForm.utils';

import type { PlanningEntityPickerOption } from '../PlanningEntityPicker.types';

const MAX_RESULTS = 100;

type UsePlanningEntitySearchOptionsParams = {
  query: string;
  /** The people types to search; none disables the search. */
  types: PlanningEntityType[];
};

/**
 * People matching the typed query, from the backend search the MRP calendar's EntitySearch uses, with the
 * same parameters: the whole tenant, MRP-labelled entities, at most 100.
 */
export const usePlanningEntitySearchOptions = ({
  query,
  types
}: UsePlanningEntitySearchOptionsParams): {
  options: PlanningEntityPickerOption[];
  isLoading: boolean;
} => {
  'use memo';

  const { t } = useTranslation('common', { keyPrefix: 'mrp' });
  const [debouncedQuery] = useDebounceValue(query, 300);
  const isEnabled = !!debouncedQuery && types.length > 0;

  const { data = [], isFetching } = queries.useFindPlanningEntities(
    debouncedQuery,
    // The backend expects comma separated strings rather than arrays, as EntitySearch passes them.
    types.join(',') as unknown as PlanningEntityType[],
    MAX_RESULTS,
    'MRP' as unknown as SchedulingLabel[],
    // No plan unit narrowing: the search covers the whole tenant.
    undefined,
    { enabled: isEnabled }
  );

  if (!query || types.length === 0) return { options: [], isLoading: false };

  const options = data
    .filter(({ id, type }) => !!id && !!type)
    .map(({ id, birthDate, label = '', number, type }) => ({
      kind: 'ENTITY' as const,
      key: `ENTITY:${type}:${id}`,
      searchOption: {
        id: id!,
        birthDate,
        label,
        number,
        group: t(`entityTypeMap.${type}.single`)
      },
      selection: {
        kind: 'ENTITY' as const,
        planningEntity: { entityId: id!, number, planningEntityType: type! },
        label
      }
    }))
    .sort((a, b) => sortLabel(a.searchOption, b.searchOption));

  // Still typing counts as loading: the results shown belong to an earlier query.
  return { options, isLoading: query !== debouncedQuery || isFetching };
};
